/*
 * Project derivations.
 *
 * Hours come from a SEPARATE endpoint (GET /api/projects/hours) as a map keyed
 * by project id, rather than being embedded in each project. Two reasons: the
 * aggregation is scoped to a date range the page controls, and a project with no
 * time yet should read as 0 rather than as a missing field.
 */

export const NEAR_BUDGET = 85;

/** Milliseconds logged against a project in the current range. */
export const hoursOf = (project, hours) => hours?.[project._id]?.ms ?? 0;

/**
 * Share of the budget used, 0 when there is no budget.
 *
 * `budgetHours` of null means "not planned", which is different from a budget of
 * zero — a project with no budget has no utilisation to speak of, so the bar is
 * replaced by the raw total rather than drawn at 100%.
 */
export function utilisation(project, hours) {
    if (!project.budgetHours) return 0;
    return (hoursOf(project, hours) / 3600000 / project.budgetHours) * 100;
}

/**
 * Colour of the utilisation bar. Over budget is not a warning, it is a fact.
 * `bar` is a TONES key for ProgressBar; `text` is a complete class string.
 */
export function budgetTone(percent) {
    if (percent >= 100) return { bar: "rose", text: "text-rose-600 dark:text-rose-400" };
    if (percent >= NEAR_BUDGET) return { bar: "amber", text: "text-amber-600 dark:text-amber-400" };
    return { bar: "emerald", text: "text-slate-500 dark:text-slate-400" };
}

export const isNearBudget = (project, hours) =>
    !project.isArchived && Boolean(project.budgetHours) && utilisation(project, hours) >= NEAR_BUDGET;

export const idOf = (value) => (typeof value === "string" ? value : (value?._id ?? null));

export const PROJECT_SORTS = {
    name: (p) => p.name.toLowerCase(),
    code: (p) => (p.code || "￿").toLowerCase(), // codeless sorts last
    created: (p) => new Date(p.createdAt).getTime(),
};

/** Sorting by hours or utilisation needs the hours map, so it is built per render. */
export function sortAccessors(hours) {
    return {
        ...PROJECT_SORTS,
        hours: (p) => hoursOf(p, hours),
        utilisation: (p) => utilisation(p, hours),
    };
}

export function filterProjects(projects, { tab, search, team, nearBudget }, hours) {
    const q = search.trim().toLowerCase();

    return projects.filter((p) => {
        if (Boolean(p.isArchived) !== (tab === "archived")) return false;
        if (team && !(p.teams ?? []).some((t) => idOf(t) === team)) return false;
        if (nearBudget && !isNearBudget(p, hours)) return false;

        if (q) {
            const haystack = `${p.name} ${p.code ?? ""} ${p.description ?? ""}`;
            if (!haystack.toLowerCase().includes(q)) return false;
        }
        return true;
    });
}

export function projectStats(projects, hours) {
    const active = projects.filter((p) => !p.isArchived);

    return {
        active: active.length,
        archived: projects.length - active.length,
        // Total across ACTIVE projects only: archived work is history, and
        // adding it in makes the current month look busier than it is.
        totalMs: active.reduce((sum, p) => sum + hoursOf(p, hours), 0),
        nearBudget: active.filter((p) => isNearBudget(p, hours)).length,
        billable: active.filter((p) => p.billable).length,
    };
}
